import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState, createContext, useContext } from 'react';
import Pagination from "../../components/Pagination";
import { PARSE_FORM_IN_ROVIEWER_FASHION } from "@/libraries/fk-data-table";
import { LabelButtonDownload } from '@/react/components/TableActionButtons'
import Spinner from "../../components/Spinner";
import EmptyState from "./components/EmptyState";
import Details from "./components/Details";
import AdditionalData from "./components/AdditionalData";
import LogInfo from "./components/LogInfo";

//quick apply search works when i was in first page but after changing the page it was not working

const AuditLogContext = createContext();
const Page = ({ payload }) => {
	const { eventSearchLists, logSearchLists, showPage, msInfo } = payload;
 
	if (!showPage)
		return <EmptyState message="No recent activity logged." />

	const [pageDataLength, setPageDataLength] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [formData, setFormData] = useState({});

	const fetchAuditLogData = async () => {
		const serializedFormData = JSON.stringify(formData);
		const url = `${API_URL}my-schedule/${msInfo.uuid}/audit-log?form_data=${serializedFormData}&page=${currentPage}&length=${pageDataLength}`;
		const response = await axios.get(url,
			{ headers: HEADER_OBJ }
		);
		return response.data;
	};

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ["auditLogs", pageDataLength, currentPage, formData],
		queryFn: fetchAuditLogData,
		placeholderData: keepPreviousData,
	});

	const handleSubmit = async (e) => {
		e.preventDefault();
		setFormData(PARSE_FORM_IN_ROVIEWER_FASHION(e.target))
	}
	// first time load, replace with a loader 
	if (isLoading)
		return <Spinner />

	return (
		<AuditLogContext.Provider value={{ data, isLoading: isFetching, handleSubmit, eventSearchLists, logSearchLists, msInfo }}>
			<SearchControls />
			<TableControls />
			<AuditLogSection />
			<Pagination totalCount={data.total_count} length={pageDataLength} currentPage={currentPage} setLength={setPageDataLength} setCurrentPage={setCurrentPage} />
		</AuditLogContext.Provider>
	)
}

const SearchControls = () => {
	// Get the context
	const { handleSubmit, eventSearchLists, logSearchLists, msInfo } = useContext(AuditLogContext);

	const loadDropdown = async () => {
		const logList = new MultiLayerDropdown("#searchLogs", { searchPlaceholder: "Type of change", levels: ["Logs"], keys: ["logs"] });
		const eventList = new MultiLayerDropdown("#searchEvents", { searchPlaceholder: "Log events", levels: ["Event"], keys: ["event"] });
		const jd = new MultiLayerDropdown("#jurisdiction", { searchPlaceholder: "Search jurisdictions", levels: ['Country', 'State'], keys: ['country', 'state'] });

		logList.initializeData(logSearchLists);
		eventList.initializeData(eventSearchLists);

		const url = `${API_URL}list/my-schedule/${msInfo.msid}`;
		fetch(`${url}/jurisdictions`, {
			method: 'GET',
			headers: HEADER_OBJ,
		}).then(res => res.json()).then(res => {
			jd.initializeData(res)
		})
	}

	useEffect(() => {
		loadDropdown()
	}, [])

	return (
		<form className={`control-center`} onSubmit={handleSubmit} id="auditlog-form" method="GET">
			<input id="search_string" name="search_string" type="text" className="input-field" placeholder="Search" />
			<div id="searchLogs" className="w-full"></div>
			<div id="searchEvents" className="w-full"></div>
			<div id="jurisdiction" className="w-full"></div>
			<button type="submit" className="btn btn-icon-wide"><span className="material-symbols-rounded">search</span></button>
			<button type="reset" className="btn btn-icon-wide"><span className="material-symbols-rounded">cached</span></button>
		</form>
	)
}

const TableControls = () => {
	const { msInfo, isLoading} = useContext(AuditLogContext);
	const handleDownload = async () => {
		try {
			const response = await fetch(`${BASE_URL}my-schedule/export-audit-log/${msInfo.uuid}`, {
				method: "GET",
				headers: HEADER_OBJ
			})
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${msInfo.name}-audit-log${new Date().toISOString().split("T")[0]}.xlsx`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
		} catch (e) {
			console.error("Download error: ", e);
		}
	}

	return <div className="table-controls mb-0" disabled={isLoading}>
		<LabelButtonDownload onClick={handleDownload} />
	</div>
}

const AuditLogSection = () => {
	const { data, isLoading } = useContext(AuditLogContext);

	return <section className="flex flex-col gap-5 pb-3" disabled={isLoading}>
		<div className="flex flex-row gap-4">
			<p className="flex flex-row gap-1 items-center text-black text-article font-medium"><span className="w-[14px] h-[14px] rounded-[3px] bg-primary"></span> New value</p>
			<p className="flex flex-row gap-1 items-center text-black text-article font-medium"><span className="w-[14px] h-[14px] rounded-[3px] bg-secondary"></span> Old value</p>
		</div>
		<RenderLogs Component={AuditLog} data={data} />
	</section>
}

const AuditLog = ({ data: { child_data: logs, date } }) => {
	return (
		<div className="flex flex-col gap-3">
			<p className="text-article text-placeholderGray font-bold">{date}</p>
			<div className="relative overflow-hidden">
				<div className="dotted-line w-full"></div>
				<div className="flex flex-col gap-11">
					{logs.map((d, i) => <Log key={i} data={d} />)}
				</div>
			</div>
		</div>
	)
}

export const RenderLogs = ({ data, Component }) => {
	if (data.total_count === 0)
		return <EmptyState message="No recent activity log found." />

	return data.data.map((d, i) => <Component key={i} data={d} />)
}

const DynamicLogComponent = ({ data }) => {
	if (data.type === 'quick_apply')
		return <AdditionalData data={data} />

	return <Details classList="text-black log-text" data={data} />
}

export const Log = ({ data, timeLength=undefined, meridiem=true }) => {
	return (
		<div className="flex gap-3 relative z-20">
			<div className="flex">
				<img src={data.avatar} alt="avatar" className="w-[55px] h-[52px] rounded-full object-cover border-normal border-secondary bg-white" />
			</div>
			<div className="flex flex-col w-full">
				<LogInfo timeLength={timeLength} meridiem={meridiem} data={data} />
				<div className="border-normal rounded-primary p-5 bg-grey-50 w-full">
					<DynamicLogComponent data={data} />
				</div>
			</div>
		</div>
	)
}

const queryClient = new QueryClient();
function initPage(selector, payload) {
	createRoot(document.querySelector(selector)).render(
		<QueryClientProvider client={queryClient}>
			<Page payload={payload} />
		</QueryClientProvider>
	);
}

export default initPage;